import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, Signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonInput,
  IonRow,
  IonTitle,
  IonToolbar,
  LoadingController,
  ModalController,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SpinnerLoadingComponent } from '@okcargo-apps/layout-ionic';
import {
  EStopStatus,
  EStopType,
  getEmptyFreightDetailedForCarrier,
  ICoords,
  IStopGoodsInfo,
} from '@okcargo-apps/shared-domain';
import { AppTab } from '@okcargo-apps/shared-domain-ionic';
import { AnalyticsService } from '@okcargo-apps/shared-infrastructure-ionic';
import { MessagesUiService } from '@okcargo-apps/messages-ui';
import { FreightCarrierInprogressCardAddressComponent } from '../freight-carrier-inprogress-card-address/freight-carrier-inprogress-card-address.component';
import { FreightCarrierInprogressCardScheduleComponent } from '../freight-carrier-inprogress-card-schedule/freight-carrier-inprogress-card-schedule.component';
import { FreightCarrierMutationsService } from '../freight-carrier-mutations.service';
import { FreightCarrierStopGeolocationComponent } from '../freight-carrier-stop-geolocation/freight-carrier-stop-geolocation.component';
import { IFreightCarrierDetailedVM } from '../freight-carrier-store.model';

@Component({
  selector: 'lib-freight-carrier-stop-finish-modal',
  templateUrl: './freight-carrier-stop-finish-modal.component.html',
  styleUrls: ['./freight-carrier-stop-finish-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonRow,
    IonGrid,
    IonCol,
    IonFooter,
    IonIcon,
    IonHeader,
    IonInput,
    IonContent,
    IonToolbar,
    IonButtons,
    IonButton,
    IonTitle,
    FreightCarrierInprogressCardAddressComponent,
    FreightCarrierInprogressCardScheduleComponent,
    SpinnerLoadingComponent,
    TranslateModule,
    ReactiveFormsModule,
    FreightCarrierStopGeolocationComponent,
  ],
})
export class FreightCarrierStopFinishModalComponent implements OnInit {
  private readonly freightCarrierMutationsService = inject(FreightCarrierMutationsService);
  private readonly fb = inject(FormBuilder);
  private readonly modalCtrl = inject(ModalController);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly messagesUiService = inject(MessagesUiService);

  readonly EStopType = EStopType;

  // comes from the component that opens the modal
  public readonly freightVM!: Signal<IFreightCarrierDetailedVM>;

  protected readonly isLoading = computed(() => this.freightVM().isLoading);

  protected readonly freightDetail = computed(() => this.freightVM().item ?? getEmptyFreightDetailedForCarrier());

  // The detailed stop extends the basic one, so a single lookup covers display and command data.
  protected readonly nextStop = computed(() =>
    this.freightDetail().stopsDetailed.find((stop) => stop.id === this.freightDetail().nextStopId),
  );

  private coords: ICoords | null = null;

  finishedStopDataForm: FormGroup;

  constructor() {
    this.finishedStopDataForm = this.fb.group({
      unloadedWeight: [''],
      unloadedPackages: [''],
      reservations: [''],
      loadedWeight: [''],
      loadedPackages: [''],
      loadedNature: [''],
      loadedSealNumber: [''],
    });

    // Side effect kept on purpose: mirrors the resolved stop into the reactive form.
    effect(() => {
      const stopDetail = this.nextStop();
      if (!stopDetail) return;

      this.finishedStopDataForm.patchValue({
        unloadedWeight: stopDetail.unloadedNetWeight ?? '',
        unloadedPackages: stopDetail.unloadedPackages ?? '',
        reservations: stopDetail.reservations ?? '',
        loadedWeight: stopDetail.netWeight ?? '',
        loadedPackages: stopDetail.packages ?? '',
        loadedNature: stopDetail.goodsDescription ?? '',
        loadedSealNumber: stopDetail.sealNumber ?? '',
      });
    });
  }

  ngOnInit(): void {
    const stopType = this.nextStop()?.type;
    this.applyValidatorsForStopType(stopType);
  }

  private applyValidatorsForStopType(stopType?: EStopType): void {
    const loadedValidators = [Validators.required];
    const unloadedValidators = [Validators.required];

    switch (stopType) {
      case EStopType.LOAD:
        this.finishedStopDataForm.get('loadedWeight')?.setValidators(loadedValidators);
        this.finishedStopDataForm.get('loadedPackages')?.setValidators(loadedValidators);
        this.finishedStopDataForm.get('loadedNature')?.setValidators(loadedValidators);
        break;
      case EStopType.UNLOAD:
        this.finishedStopDataForm.get('unloadedWeight')?.setValidators(unloadedValidators);
        this.finishedStopDataForm.get('unloadedPackages')?.setValidators(unloadedValidators);
        break;
      case EStopType.UNLOAD_LOAD:
        this.finishedStopDataForm.get('loadedWeight')?.setValidators(loadedValidators);
        this.finishedStopDataForm.get('loadedPackages')?.setValidators(loadedValidators);
        this.finishedStopDataForm.get('loadedNature')?.setValidators(loadedValidators);
        this.finishedStopDataForm.get('unloadedWeight')?.setValidators(unloadedValidators);
        this.finishedStopDataForm.get('unloadedPackages')?.setValidators(unloadedValidators);
        break;
    }

    this.finishedStopDataForm.updateValueAndValidity();
  }

  public onCoordsChange(coords: ICoords | null): void {
    this.coords = coords;
  }

  cancel(): Promise<boolean> {
    return this.modalCtrl.dismiss(null, 'cancel');
  }

  // TODO check if all info is sent ok
  async confirm(): Promise<boolean> {
    if (!this.finishedStopDataForm.valid) {
      this.finishedStopDataForm.markAllAsTouched();
      return true;
    }

    const loading = await this.loadingCtrl.create({
      message: this.translate.instant('general.send_data'),
    });
    loading.present();

    const freightId = this.freightDetail().id;
    const nextStop = this.nextStop();

    try {
      if (nextStop !== undefined) {
        const stopGoodsInfo: IStopGoodsInfo = {
          freightId,
          stopId: nextStop.id,
          tare: null,
          grossWeight: null,
          netWeight: this.finishedStopDataForm.get('loadedWeight')?.value || '',
          packages: this.finishedStopDataForm.get('loadedPackages')?.value || '',
          unloadedNetWeight: this.finishedStopDataForm.get('unloadedWeight')?.value || '',
          unloadedPackages: this.finishedStopDataForm.get('unloadedPackages')?.value || '',
          goodsDescription: this.finishedStopDataForm.get('loadedNature')?.value || '',
          reservations: this.finishedStopDataForm.get('reservations')?.value || '',
          sealNumber: this.finishedStopDataForm.get('loadedSealNumber')?.value || '',
        };

        await this.freightCarrierMutationsService.modifyStopStatus(
          nextStop,
          this.freightDetail(),
          EStopStatus.FINISHED,
          this.coords ?? undefined,
        );
        await this.freightCarrierMutationsService.sendStopGoodsInfo(stopGoodsInfo);

        this.analyticsService.logEvent('stop_status_finished');

        this.messagesUiService.setMessage({
          objectId: freightId,
          message: [this.translate.instant('freight.detail.stop_finished_success')],
        });

        await new Promise((resolve) => setTimeout(resolve, 1500));

        const freightDetail = this.freightDetail();
        if (
          freightDetail.nextStopStatus === EStopStatus.STARTED &&
          freightDetail.nextStopType === EStopType.UNLOAD &&
          freightDetail.nextStopId === freightDetail.stopsDetailed[freightDetail.stopsDetailed.length - 1].id
        ) {
          this.router.navigate([`/tabs/${AppTab.FINISHED}`, freightDetail.id]);
        }
      }
    } catch (error) {
      this.messagesUiService.setMessage({
        objectId: freightId,
        message: [this.translate.instant('freight.detail.stop_finished_error')],
      });
      console.error('Error finishing stop:', error);
    } finally {
      await loading.dismiss();
    }

    return this.modalCtrl.dismiss(null, 'confirm');
  }
}
